import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { UserPlus, Trash, X, User } from 'lucide-react';
import { useClient } from '../ClientContext';
import { authApiClient } from '../hooks/useAuthInterceptor';

const MODAL_TYPE_ADD = 'Add New User';
const MODAL_TYPE_UPDATE = 'Update User';
const MODAL_TYPE_DELETE = 'Delete User';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UserManagementModal = ({
  modalType,
  open,
  onClose,
  onSuccess,
  selectedUser,
  accessToken,
  accountTypeUsers = [],
}) => {
  const styles = {
    dialogTitleBox: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      p: '32px 16px',
    },
    iconBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    deleteContent: {
      padding: '32px',
    },
    formBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '16px 32px',
    },
    footerBox: (modalType) => ({
      display: 'flex',
      justifyContent: modalType === MODAL_TYPE_DELETE ? 'flex-end' : 'space-between',
      alignItems: 'center',
      px: '16px',
      pb: '32px',
      pt: '16px',
    }),
    requiredFieldText: {
      fontStyle: 'italic',
      color: 'red',
    },
    cancelButton: {
      color: '#475467',
      borderColor: '#475467',
      textTransform: 'none',
      boxShadow: 'none',
      fontWeight: 'bold',
    },
    actionButton: {
      backgroundColor: '#772EDD',
      textTransform: 'none',
      boxShadow: 'none',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: '#5C21BF',
      },
    },
    checkbox: {
      '&.Mui-checked': {
        color: '#772EDD',
      },
    },
    dialog: {
      borderRadius: '8px',
    },
    deleteUserText: {
      mb: 2,
    },
    userNameText: {
      fontWeight: 700,
      color: '#772EDD',
      mb: 2,
    },
    confirmationText: {
      color: '#475467',
    },
    requiredField: {
      '& .MuiFormLabel-asterisk': {
        color: 'red',
        ml: '-1px',
      },
    },
    dialogActions: {
      justifyContent: 'flex-end',
    },
    warningText: {
      color: '#DC6803',
      fontSize: '14px',
      mt: '-8px',
      lineHeight: '22px',
    },
    warningBorder: {
      borderColor: '#DC6803',
    },
    accountBreakdownGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      border: '1px solid #EAECF0',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: '#FFFFFF',
    },
    accountBreakdownCard: {
      p: '16px',
      borderRight: '1px solid #EAECF0',
      '&:last-of-type': {
        borderRight: 'none',
      },
    },
    accountBreakdownLabel: {
      color: '#667085',
      fontSize: '13px',
      lineHeight: '20px',
      mb: '6px',
    },
    accountBreakdownPercent: {
      color: '#101828',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '28px',
    },
    accountBreakdownCount: {
      color: '#667085',
      fontSize: '12px',
      lineHeight: '18px',
    },
  };

  // Mapping provided by central config: roleIdToDisplayName

  const { selectedClient, config } = useClient();
  const supportsDemoMode = config?.clients?.[selectedClient]?.supportsDemoMode === true;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [password, setPassword] = useState('');
  const [isPasswordWeak, setIsPasswordWeak] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [includeUsernamePassword, setIncludeUsernamePassword] = useState(false);
  const [grantCaseAssigner, setGrantCaseAssigner] = useState(true);
  const [internal, setInternal] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [fetchedRoleId, setFetchedRoleId] = useState(null);
  const modalInitializationKeyRef = useRef(null);
  const roleFetchRequestIdRef = useRef(0);
  const roleManuallySelectedRef = useRef(false);

  const getAvailableRoles = useCallback(() => {
    const byClient = (config && config.availableRolesByClient) || {};
    return byClient[selectedClient] || ['Admin'];
  }, [config, selectedClient]);

  const getDefaultRole = useCallback(() => {
    return getAvailableRoles()[0] || '';
  }, [getAvailableRoles]);

  // Soft warning only: admins can still submit if the domain mismatch is intentional.
  const updateEmailWarning = useCallback(
    (emailValue, selectedRole) => {
      if (emailValue.includes('@')) {
        const emailDomain = emailValue.substring(emailValue.indexOf('@'));
        const roleDomainMapping = (config && config.roleDomainMapping) || {};
        const expectedDomain = roleDomainMapping[selectedRole] || '';
        setShowEmailWarning(expectedDomain && emailDomain !== expectedDomain.toLowerCase());
      } else {
        setShowEmailWarning(false);
      }
    },
    [config],
  );

  const clearForm = useCallback(() => {
    setName('');
    setEmail('');
    setRole(getDefaultRole());
    setFetchedRoleId(null);
    roleManuallySelectedRef.current = false;
    setShowEmailWarning(false);
    setIsPasswordWeak(false);
    setPassword('');
    setIncludeUsernamePassword(false);
    setGrantCaseAssigner(true);
    setInternal(false);
    setDemoMode(false);
    setValidationErrors({});
  }, [getDefaultRole]);

  useEffect(() => {
    if (!open) {
      modalInitializationKeyRef.current = null;
      roleFetchRequestIdRef.current += 1;
      return;
    }

    // Initialize each modal/user once. Config refreshes must not erase in-progress input.
    const initializationKey = `${modalType}:${selectedClient}:${selectedUser?.id || 'new'}`;
    if (modalInitializationKeyRef.current === initializationKey) {
      return;
    }
    modalInitializationKeyRef.current = initializationKey;
    const roleFetchRequestId = ++roleFetchRequestIdRef.current;

    if (modalType === MODAL_TYPE_UPDATE && selectedUser) {
      setName(selectedUser.name);
      setEmail(selectedUser.email.toLowerCase());
      setDemoMode(!!selectedUser.demo_mode);
      setRole('');
      setFetchedRoleId(null);
      roleManuallySelectedRef.current = false;

      const fetchRole = async () => {
        try {
          const response = await authApiClient.get(`/admin/user/${selectedUser.auth0_id}/role`);
          if (roleFetchRequestId !== roleFetchRequestIdRef.current) {
            return;
          }
          setFetchedRoleId(response.data.roleId);
        } catch (error) {
          if (roleFetchRequestId !== roleFetchRequestIdRef.current) {
            return;
          }
          // eslint-disable-next-line no-console
          console.error('Error fetching user role:', error);
          alert('Failed to fetch user role. Name and email can still be updated.');
        }
      };

      fetchRole();
    } else if (modalType === MODAL_TYPE_ADD) {
      clearForm();
    }
  }, [clearForm, modalType, open, selectedClient, selectedUser]);

  // Keep only untouched role selections in sync as client configuration becomes available.
  useEffect(() => {
    if (!open || roleManuallySelectedRef.current) {
      return;
    }

    if (modalType === MODAL_TYPE_ADD) {
      setRole(getDefaultRole());
    } else if (modalType === MODAL_TYPE_UPDATE && fetchedRoleId) {
      const nameMap = (config && config.roleIdToDisplayName) || {};
      const fetchedRoleName = nameMap[fetchedRoleId];
      if (fetchedRoleName) {
        setRole(fetchedRoleName);
      }
    }
  }, [config, fetchedRoleId, getDefaultRole, modalType, open]);

  // Recalculate when either side changes because the expected email domain is role-dependent.
  useEffect(() => {
    updateEmailWarning(email, role);
  }, [email, role, updateEmailWarning]);

  const validatePassword = (password) => {
    const isStrong = password.length >= 8 && /[0-9]/.test(password) && /[!@#$%^&*(),.?":{}|<>]/.test(password);
    setIsPasswordWeak(!isStrong);
  };

  const handlePasswordChange = (event) => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);
    validatePassword(passwordValue);
  };

  const handleEmailChange = (event) => {
    const emailValue = event.target.value;
    setEmail(emailValue);
  };

  const validateForm = () => {
    const errors = {};

    if (!name.trim()) {
      errors.name = 'Name is required';
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      errors.email = 'Email is required';
    } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
      errors.email = 'Enter a valid email address';
    }

    if (modalType === MODAL_TYPE_ADD && !role) {
      errors.role = 'User role is required';
    }

    if (includeUsernamePassword) {
      if (!password) {
        errors.password = 'Password is required';
      } else if (isPasswordWeak) {
        errors.password = 'Password is too weak (minimum 8 characters, 1 number, 1 special character)';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Auth0 IDs include the connection prefix, which is the most reliable local signal for account type.
  const getAccountType = (auth0Id = '') => {
    if (auth0Id.startsWith('auth0|')) {
      return 'usernamePassword';
    }
    if (auth0Id.startsWith('google-oauth2|')) {
      return 'google';
    }
    if (auth0Id.startsWith('oidc|')) {
      return 'oidc';
    }
    return 'pendingUnlinked';
  };

  // accountTypeUsers is already scoped to the selected company by the parent component.
  const accountBreakdown = accountTypeUsers.reduce(
    (acc, user) => {
      const accountType = getAccountType(user.auth0_id || '');
      acc[accountType] += 1;
      acc.total += 1;
      return acc;
    },
    { usernamePassword: 0, google: 0, oidc: 0, pendingUnlinked: 0, total: 0 },
  );

  const formatAccountPercentage = (count) => {
    if (!accountBreakdown.total) {
      return '0%';
    }
    return `${Math.round((count / accountBreakdown.total) * 100)}%`;
  };

  const accountBreakdownCards = [
    {
      label: 'Username/Password',
      count: accountBreakdown.usernamePassword,
      percent: formatAccountPercentage(accountBreakdown.usernamePassword),
    },
    {
      label: 'Google',
      count: accountBreakdown.google,
      percent: formatAccountPercentage(accountBreakdown.google),
    },
    {
      label: 'OIDC',
      count: accountBreakdown.oidc,
      percent: formatAccountPercentage(accountBreakdown.oidc),
    },
    {
      label: 'Pending/Unlinked',
      count: accountBreakdown.pendingUnlinked,
      percent: formatAccountPercentage(accountBreakdown.pendingUnlinked),
    },
  ];

  const isFormReady =
    Boolean(name.trim()) &&
    EMAIL_PATTERN.test(email.trim()) &&
    (modalType !== MODAL_TYPE_ADD || Boolean(role)) &&
    (!includeUsernamePassword || (Boolean(password) && !isPasswordWeak));

  const handleSubmit = async () => {
    try {
      if (updating) return;

      if (!validateForm()) {
        return;
      }

      setUpdating(true);

      const mapDisplayToKey = (config && config.roleDisplayToAuth0Key) || {};
      const mappedRole = role ? mapDisplayToKey[role] || role : undefined;

      const data = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role: mappedRole,
        password,
        includeUsernamePassword,
        grantCaseAssigner,
        internal,
        client: selectedClient,
        ...(supportsDemoMode ? { demoMode } : {}),
      };

      const response =
        modalType === MODAL_TYPE_UPDATE
          ? await authApiClient.patch(`/admin/user/${selectedUser.id}`, data)
          : await authApiClient.post('/admin/create-user', data);

      if (response.status === 200 || response.status === 201) {
        alert(modalType === MODAL_TYPE_UPDATE ? 'User updated successfully' : 'User created successfully');
        await onSuccess?.();
        clearForm();
        onClose();
      }
      setUpdating(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error with ${modalType === MODAL_TYPE_UPDATE ? 'updating' : 'creating'} user:`, error.message);
      alert(error.response?.data?.message || `Failed to ${modalType === MODAL_TYPE_UPDATE ? 'update' : 'create'} user`);
      setUpdating(false);
    }
  };

  const renderIcon = () => {
    switch (modalType) {
      case MODAL_TYPE_ADD:
        return <UserPlus size={24} />;
      case MODAL_TYPE_DELETE:
        return <Trash size={24} />;
      case MODAL_TYPE_UPDATE:
        return <User size={24} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        clearForm();
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      sx={styles.dialog}
    >
      <DialogTitle sx={{ padding: 0 }}>
        <Box sx={styles.dialogTitleBox}>
          <Box sx={styles.iconBox}>
            {renderIcon()}
            <Typography variant="h5" fontWeight="bold">
              {modalType}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              clearForm();
              onClose();
            }}
            size="small"
          >
            <X size={24} />
          </IconButton>
        </Box>
      </DialogTitle>
      {modalType === MODAL_TYPE_DELETE ? (
        <DialogContent dividers sx={styles.deleteContent}>
          <Typography sx={styles.deleteUserText}>You are about to delete this user:</Typography>
          <Typography sx={styles.userNameText}>
            {selectedUser?.name} ({selectedUser?.email})
          </Typography>
          <Typography sx={styles.confirmationText}>
            You cannot undo this action. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
      ) : (
        <DialogContent dividers sx={{ padding: 0 }}>
          <Box component="form" sx={styles.formBox}>
            {modalType === MODAL_TYPE_ADD && (
              <Box sx={styles.accountBreakdownGrid}>
                {accountBreakdownCards.map((card) => (
                  <Box key={card.label} sx={styles.accountBreakdownCard}>
                    <Typography sx={styles.accountBreakdownLabel}>{card.label}</Typography>
                    <Typography sx={styles.accountBreakdownPercent}>{card.percent}</Typography>
                    <Typography sx={styles.accountBreakdownCount}>
                      {card.count} of {accountBreakdown.total || 0} users
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <TextField
              label="Name"
              placeholder="Enter full name of user"
              fullWidth
              required
              sx={styles.requiredField}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (validationErrors.name) {
                  setValidationErrors((prev) => ({ ...prev, name: '' }));
                }
              }}
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name || ''}
            />
            <TextField
              label="Email"
              placeholder="Enter email of user"
              fullWidth
              required
              value={email}
              onChange={(event) => {
                handleEmailChange(event);
                if (validationErrors.email) {
                  setValidationErrors((prev) => ({ ...prev, email: '' }));
                }
              }}
              sx={{ ...styles.requiredField, ...(showEmailWarning ? styles.warningBorder : {}) }}
              error={Boolean(validationErrors.email)}
              helperText={validationErrors.email || ''}
            />
            {showEmailWarning && (
              <Typography sx={styles.warningText}>
                Are you sure this is the correct email ({email.substring(email.indexOf('@'))}) for the {role} dashboard?
              </Typography>
            )}
            <FormControl
              fullWidth
              required={modalType === MODAL_TYPE_ADD}
              error={Boolean(validationErrors.role)}
              sx={styles.requiredField}
            >
              <InputLabel>User Role</InputLabel>
              <Select
                value={role}
                onChange={(e) => {
                  roleManuallySelectedRef.current = true;
                  setRole(e.target.value);
                  if (validationErrors.role) {
                    setValidationErrors((prev) => ({ ...prev, role: '' }));
                  }
                }}
                label="User Role"
              >
                {getAvailableRoles().map((roleOption) => (
                  <MenuItem key={roleOption} value={roleOption}>
                    {roleOption}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.role && (
                <Typography variant="caption" color="error" sx={{ mt: '3px', mx: '14px' }}>
                  {validationErrors.role}
                </Typography>
              )}
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeUsernamePassword}
                  onChange={(e) => setIncludeUsernamePassword(e.target.checked)}
                  sx={styles.checkbox}
                />
              }
              label="Include username/password authentication"
            />
            {includeUsernamePassword && (
              <TextField
                label="Password"
                type="password"
                placeholder="Enter a strong password"
                fullWidth
                required
                sx={styles.requiredField}
                value={password}
                onChange={(event) => {
                  handlePasswordChange(event);
                  if (validationErrors.password) {
                    setValidationErrors((prev) => ({ ...prev, password: '' }));
                  }
                }}
                error={Boolean(validationErrors.password) || isPasswordWeak}
                helperText={
                  validationErrors.password ||
                  (isPasswordWeak ? 'Password is too weak (minimum 8 characters, 1 number, 1 special character)' : '')
                }
              />
            )}
            {modalType === MODAL_TYPE_ADD && selectedClient === 'SoundExchange' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={grantCaseAssigner}
                    onChange={(e) => setGrantCaseAssigner(e.target.checked)}
                    sx={styles.checkbox}
                  />
                }
                label="Grant Case Assigner permissions"
              />
            )}
            <FormControlLabel
              control={
                <Checkbox checked={internal} onChange={(e) => setInternal(e.target.checked)} sx={styles.checkbox} />
              }
              label="Internal (hidden from other Dashboard users)"
            />
            {supportsDemoMode && (
              <FormControlLabel
                control={
                  <Checkbox checked={demoMode} onChange={(e) => setDemoMode(e.target.checked)} sx={styles.checkbox} />
                }
                label="Demo mode (blur identifying text in dashboard)"
              />
            )}
          </Box>
        </DialogContent>
      )}
      <Box sx={styles.footerBox(modalType)}>
        {modalType !== MODAL_TYPE_DELETE && <Typography sx={styles.requiredFieldText}>* Required field</Typography>}
        <DialogActions sx={styles.dialogActions}>
          {modalType === MODAL_TYPE_DELETE ? (
            <>
              <Button
                onClick={() => {
                  clearForm();
                  onClose();
                }}
                variant="outlined"
                sx={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button onClick={onClose} variant="contained" color="error">
                Delete User
              </Button>
            </>
          ) : (
            <>
              <Button onClick={clearForm} variant="outlined" sx={styles.cancelButton}>
                Clear All
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                sx={styles.actionButton}
                disabled={updating || !isFormReady}
              >
                {modalType === MODAL_TYPE_ADD ? 'Add User' : 'Update User'}
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default UserManagementModal;
